import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./ComplaintsTimelineChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import Notifications330Service from "../Notifications330Service";
import { Notifications330FilterModel } from "../Notifications330FilterModel";
import { ComplaintsTimelineReadModel } from "../Notifications330ReadModel";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    reverseMonitorDashboardTimelineSeries,
    MONITOR_CHART_GRID_COLOR,
} from "../../chart/monitorDashboardChartKit";
import { saveNotifications330FilterToStorage, NOTIFICATIONS330_DETAILS_ROUTES, buildNotifications330ScopePayload } from "../notifications330DetailsShared";

const MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

@WithRender
@Component({
    diProvide: [Notifications330Service],
})
export default class ComplaintsTimelineChart extends Vue {
    @Inject(Notifications330Service)
    public notifications330Service!: Notifications330Service;

    @Prop({ required: true }) public filter!: Notifications330FilterModel;

    private chart: any = null;
    private canvasId: string = "complaints-timeline-chart-canvas";

    public hasData: boolean = false;
    public isLoading: boolean = false;
    private barCount: number = 0;
    private requestVersion: number = 0;

    public get scrollContainerStyle(): Record<string, string> {
        const overflowX = this.barCount > 12 ? "auto" : "hidden";
        return { overflowX: overflowX, overflowY: "hidden" };
    }

    public get innerStyle(): Record<string, string> {
        if (this.barCount <= 12) {
            return {
                width: "100%",
                height: "100%",
                minWidth: "0",
                maxWidth: "100%",
            };
        }
        const minWidthPx = Math.max(600, this.barCount * 48);
        return {
            width: minWidthPx + "px",
            minWidth: minWidthPx + "px",
            height: "100%",
        };
    }

    public onTitleClick(): void {
        this.navigateToDetails(null, null);
    }

    private navigateToDetails(year: number | null, month: number | null): void {
        if (!this.filter) {
            return;
        }

        if (year != null && month != null) {
            saveNotifications330FilterToStorage(this.filter, undefined, year, month);
        } else {
            saveNotifications330FilterToStorage(this.filter);
        }

        this.$router.push({ name: NOTIFICATIONS330_DETAILS_ROUTES.timeline });
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const payload = buildNotifications330ScopePayload(this.filter);

        const resp = await this.notifications330Service.GetComplaintsTimeline(payload);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;

        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.barCount = 0;
            this.destroyChart();
            return;
        }

        const rows = resp.result as ComplaintsTimelineReadModel[];
        this.barCount = rows.length;

        let hasAnyNonZero = false;
        for (let i = 0; i < rows.length; i++) {
            if (Number(rows[i].count) !== 0) {
                hasAnyNonZero = true;
                break;
            }
        }
        this.hasData = hasAnyNonZero;

        await this.$nextTick();
        if (!this.hasData) {
            this.destroyChart();
            return;
        }
        this.renderChart(rows);
    }

    public beforeDestroy(): void {
        this.destroyChart();
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    private formatMonthLabel(year: number, month: number): string {
        const monthIndex = month >= 1 && month <= 12 ? month - 1 : 0;
        const shortYear = year >= 2000 ? String(year).slice(-2) : String(year);
        return MONTH_NAMES[monthIndex] + " " + shortYear;
    }

    private renderChart(rows: ComplaintsTimelineReadModel[]): void {
        const labels: string[] = [];
        const data: number[] = [];
        const timelineRows: ComplaintsTimelineReadModel[] = [];

        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            labels.push(this.formatMonthLabel(Number(r.year), Number(r.month)));
            data.push(Number(r.count));
            timelineRows.push(r);
        }

        reverseMonitorDashboardTimelineSeries(labels, data, timelineRows);

        this.destroyChart();

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;
        const self = this;
        const lineColor = getMonitorDashboardChartColor("complaintsTimeline");
        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();
        const countLabel = String(this.$t("monitorDashboard.complaintsCountLabel"));
        const yScale = this.computeYAxisScale(data);
        const chartTimelineRows = timelineRows;
        const chartData = data;

        this.chart = new Chart(ctx, {
            type: "line",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "",
                        data: data,
                        borderColor: lineColor,
                        backgroundColor: lineColor,
                        borderWidth: 2,
                        fill: false,
                        pointBackgroundColor: lineColor,
                        pointBorderColor: lineColor,
                        pointBorderWidth: 1,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        lineTension: 0,
                    },
                ],
            },
            plugins: [
                {
                    afterDatasetsDraw: function (chartInstance: any) {
                        const ctx2 = chartInstance.ctx;
                        chartInstance.data.datasets.forEach(function (dataset: any, i: number) {
                            const meta = chartInstance.getDatasetMeta(i);
                            if (!meta.hidden) {
                                meta.data.forEach(function (point: any, j: number) {
                                    const value = dataset.data[j];
                                    const x = point._model.x;
                                    const y = point._model.y;
                                    ctx2.save();
                                    ctx2.textAlign = "center";
                                    ctx2.textBaseline = "bottom";
                                    ctx2.font = "11px " + fontFamily;
                                    ctx2.fillStyle = "#6b7280";
                                    ctx2.fillText(String(value), x, y - 8);
                                    ctx2.restore();
                                });
                            }
                        });
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                layout: {
                    padding: { top: 16, right: 8, bottom: 0, left: 8 },
                },
                onClick: function (evt: any, elements: any[]) {
                    if (!elements || elements.length === 0) {
                        return;
                    }
                    const idx = elements[0]._index;
                    const count =
                        chartData[idx] != null ? Number(chartData[idx]) : 0;
                    if (isNaN(count) || count <= 0) {
                        return;
                    }
                    const row = chartTimelineRows[idx];
                    if (!row) {
                        return;
                    }
                    self.navigateToDetails(Number(row.year), Number(row.month));
                },
                legend: {
                    display: false,
                },
                scales: {
                    xAxes: [
                        {
                            gridLines: { display: false },
                            ticks: buildMonitorDashboardCategoryAxisTicks(11, fontFamily, 45),
                        },
                    ],
                    yAxes: [
                        {
                            gridLines: { color: MONITOR_CHART_GRID_COLOR },
                            ticks: buildMonitorDashboardValueAxisTicks(
                                11,
                                fontFamily,
                                true,
                                yScale.max,
                                yScale.step,
                            ),
                        },
                    ],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    callbacks: {
                        title: function (tooltipItems: any[]) {
                            if (!tooltipItems || tooltipItems.length === 0) return "";
                            const idx = tooltipItems[0].index;
                            return labels[idx];
                        },
                        label: function (tooltipItem: any, chartData: any) {
                            const idx = tooltipItem.index;
                            const lines: string[] = [];
                            let total = 0;
                            for (let i = 0; i < chartData.datasets.length; i++) {
                                const ds = chartData.datasets[i];
                                const raw =
                                    ds && ds.data && ds.data.length > idx
                                        ? Number(ds.data[idx])
                                        : 0;
                                const value = isNaN(raw) ? 0 : raw;
                                total += value;
                                const dsLabel =
                                    ds && ds.label
                                        ? String(ds.label)
                                        : countLabel;
                                lines.push(dsLabel + ": " + String(value));
                            }
                            if (chartData.datasets.length > 1) {
                                lines.push(
                                    String(self.$t("monitorDashboard.total")) + ": " + String(total),
                                );
                            }
                            return lines;
                        },
                    },
                }),
            }),
        });

        const chartRef = this.chart;
        this.$nextTick(function () {
            if (chartRef && chartRef.resize) {
                chartRef.resize();
            }
        });
    }

    private computeYAxisScale(values: number[]): { max: number; step: number } {
        let maxVal = 0;
        for (let i = 0; i < values.length; i++) {
            const v = Number(values[i]);
            if (!isNaN(v) && v > maxVal) {
                maxVal = v;
            }
        }
        if (maxVal <= 0) {
            return { max: 10, step: 2 };
        }

        if (maxVal <= 100) {
            const smallMax = Math.ceil(maxVal * 1.5);
            const smallStep = this.getNiceStep(Math.max(1, Math.ceil(smallMax / 5)));
            return {
                max: Math.ceil(smallMax / smallStep) * smallStep,
                step: smallStep,
            };
        }

        const padded = maxVal * 1.08;
        let roundUnit = 1000;
        if (maxVal < 200) {
            roundUnit = 20;
        } else if (maxVal < 2000) {
            roundUnit = 100;
        } else if (maxVal < 20000) {
            roundUnit = 500;
        }
        const max = Math.ceil(padded / roundUnit) * roundUnit;
        const step = this.getNiceStep(Math.max(1, Math.floor(max / 5)));
        return { max: max, step: step };
    }

    private getNiceStep(rawStep: number): number {
        if (rawStep <= 1) {
            return 1;
        }
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalized = rawStep / magnitude;
        if (normalized <= 1) {
            return magnitude;
        }
        if (normalized <= 2) {
            return 2 * magnitude;
        }
        if (normalized <= 5) {
            return 5 * magnitude;
        }
        return 10 * magnitude;
    }
}
